import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createService } from '../../../api/createServiceApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  getAuthKey,
  getClaimNumber,
  getPartnerId,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getZipCode,
} from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import {
  updateSelectedProvidersState,
  updateStageState,
} from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { useActions } from '../../../app/store/useActions';
import { backBtn, goToClaimsBtn, selectThisPartnerBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import {
  CreateServicePayload,
  CreateServiceResponse,
  OpCo,
  PartnerId,
  PropertyServicesInfo,
  ProviderInfo,
  ServiceType,
  SubServices,
} from '../../../types/type';
import { getPrettyServiceName, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import ServiceCard from '../../shared/ServiceCard/ServiceCard';
import ServiceError from '../../shared/ServiceErrorScreen/ServiceError';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

interface EmergencyServicesSelection {
  headerContent?: string;
  serviceType?: ServiceType;
}

export default function EmergencyServicesSelection() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { headerContent, serviceType } = state as EmergencyServicesSelection;
  const [providerID, setProviderID] = React.useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [serviceError, setServiceError] = React.useState(false);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  let zipCode = useSelector(getZipCode);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  // If the zip code is not present in validate call response, fallback to the one which is in claim details response
  if (!zipCode) {
    zipCode = claimSummary?.lossLocationAddress.postalCode;
  }
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const currentStage = availableProgressStages.filter((stage) => stage.viewActive === true);

  const eligibleWaterServiceInfo = useAppSelector((state) => state.services.eligibleWaterService);
  const eligibleEmergencyServiceInfo = useAppSelector((state) => state.services.eligibleEmergencyService);

  let eligibleServiceInfo: PropertyServicesInfo | undefined;
  if (serviceType === ServiceType.EMERGENCY_SERVICE_TYPE) {
    eligibleServiceInfo = eligibleEmergencyServiceInfo;
  } else if (serviceType === ServiceType.WATER_MITIGATION_TYPE) {
    eligibleServiceInfo = eligibleWaterServiceInfo;
  }

  const selectedProviderInfo = eligibleServiceInfo?.providers.filter((provider) => provider.providerID === providerID);

  async function handleNext() {
    // Make web service call to create a service
    const response = await updateServiceSelection();
    // Hide LoadingIndicator
    setModalLoadingOpen(false);
    if (response) {
      // Once success, update the local store
      updateLocalStore();
      // Go to the next screen
      gotoNextScreen();
    } else {
      //Show UI for service error handling
      setServiceError(true);
    }
  }

  async function updateServiceSelection() {
    // Show LoadingIndicator
    setModalLoadingOpen(true);
    let subServices: SubServices[] = [];
    if (selectedProviderInfo) {
      subServices = getSubServicesCollection(selectedProviderInfo);
    }
    const postBody: CreateServicePayload = {
      claimNumber: claimNumber,
      zipCode: zipCode,
      serviceType: eligibleServiceInfo?.serviceType ? eligibleServiceInfo?.serviceType : '',
      serviceTypeCode: eligibleServiceInfo?.serviceTypeCode ? eligibleServiceInfo?.serviceTypeCode : '',
      vendorName: selectedProviderInfo ? selectedProviderInfo[0].providerName : '',
      subServices: subServices ? subServices : undefined,
      plnar: false, // Damage documentation with Plnar is false with other service selections - always
    };
    // Remove the subServices collection if it is empty
    if (subServices && subServices.length === 0) {
      delete postBody.subServices;
    }
    const response: CreateServiceResponse = await createService(urlKey, sessionId, authKey, postBody);
    if (response && response.response.updatedAuthToken) {
      contextRouterActions.updateAuthToken(response.response.updatedAuthToken);
    }
    return response;
  }

  function getSubServicesCollection(providerInfo: ProviderInfo[]) {
    const subServices: SubServices[] = [];
    if (providerInfo[0] && providerInfo[0].subServiceTypes) {
      providerInfo[0].subServiceTypes.forEach((element) => {
        const typeCode = element.subServiceTypeCode;
        const subService = { code: typeCode };
        subServices.push(subService);
      });
    }
    return subServices;
  }

  function updateLocalStore() {
    // Update provider's information in local store too, since the remote update was success.
    if (selectedProviderInfo && selectedProviderInfo[0]) {
      const selectedProvider = {
        ...selectedProviderInfo[0],
        serviceTypeName: serviceType ? getPrettyServiceName(serviceType) : '', // Friendly service name is not provided in the service response. Hence adding it here.
      };
      dispatch(
        updateSelectedProvidersState({
          selectedProvider: selectedProvider,
        })
      );
    }

    // Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === currentStage[0].id
          ? {
              ...stage,
              completed: true,
              viewActive: true,
              showProgress: false,
              skipped: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
  }

  function gotoNextScreen() {
    if (selectedProviderInfo && selectedProviderInfo[0]) {
      navigate('/single-service-confirmation', {
        state: {
          provider: selectedProviderInfo[0],
        },
      });
    }
  }

  function handleBack() {
    navigate(-1);
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }

  function providerListItemClickHandler(providerID: string) {
    setProviderID(providerID);
  }
  let content = <div></div>;

  if (!serviceError) {
    content = (
      <Box sx={{ width: '100%' }}>
        <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
        {eligibleServiceInfo?.providers && (
          <PropertyServiceInfo
            headerContent={headerContent}
            infoContent={
              <ServiceCard
                providers={eligibleServiceInfo?.providers}
                providerListItemClickHandler={providerListItemClickHandler}
                checkedValue={providerID}
              ></ServiceCard>
            }
          ></PropertyServiceInfo>
        )}

        <Grid
          container
          sx={{
            width: '100%',
            maxWidth: '1280px',
            minWidth: '310px',
            margin: '0 auto',
            marginTop: '1em',
          }}
        >
          <Grid item xs={12}>
            <CallToAction
              nextButton={true}
              nextButtonDisabled={providerID ? false : true}
              nextButtonContent={selectThisPartnerBtn}
              handleNext={handleNext}
              skipButton={false}
              backButton={true}
              backButtonContent={backBtn}
              handleBack={handleBack}
              cancelButton={true}
              cancelButtonContent={goToClaimsBtn}
              handleCancel={goToClaimsOverview}
            ></CallToAction>
          </Grid>
        </Grid>
        {modalLoadingOpen && <ModalLinearActivityIndicator />}
      </Box>
    );
  } else {
    content = <Box sx={{ width: '100%' }}>{serviceError && <ServiceError />}</Box>;
  }

  return <Box sx={{ width: '100%' }}>{content}</Box>;
}
